import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { type JSONContent } from '@tiptap/core';
import {
  ArrowLeft,
  Check,
  Cloud,
  History as HistoryIcon,
  Loader2,
  RefreshCw,
  Save,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useTree } from '@/context/TreeContext';
import Editor from '@/components/Editor';
import StatusBadge from '@/components/StatusBadge';
import VersionHistory from '@/components/VersionHistory';

/**
 * API contract used here:
 *   - api.getNode(id) -> { id, title, body(TipTap JSON), version, _etag, updatedAt?, ... }
 *   - api.updateNode(id, { title?, body?, expectedEtag? }) -> node   (409 { error:'stale', current })
 * Both are part of the documented contract. The 409 is handled whether the client returns the
 * `{ error:'stale' }` body OR throws an error carrying status 409.
 */
interface NodeDoc {
  id: string;
  title: string;
  status?: string;
  body: JSONContent | null;
  contentType?: 'tiptap' | 'html';
  html?: string;
  version?: number;
  _etag?: string;
  updatedAt?: string;
  updatedBy?: string;
  [k: string]: unknown;
}

interface UpdateResult extends NodeDoc {
  error?: string;
  current?: NodeDoc;
}

interface WikiApi {
  getNode: (id: string) => Promise<NodeDoc>;
  updateNode: (
    id: string,
    patch: {
      title?: string;
      status?: string;
      body?: JSONContent | null;
      contentType?: 'tiptap' | 'html';
      html?: string;
      expectedEtag?: string;
    },
  ) => Promise<UpdateResult>;
}

const AUTOSAVE_LOCAL_MS = 1500; // debounce → localStorage
const AUTOSAVE_SERVER_MS = 6000; // cadence → server PUT

type SaveState = 'idle' | 'unsaved' | 'saving' | 'saved' | 'conflict' | 'error';

interface DraftPayload {
  title: string;
  status?: string;
  body: JSONContent | null;
  contentType?: 'tiptap' | 'html';
  html?: string;
  savedAt: number;
  baseVersion?: number;
}

function draftKey(id: string): string {
  return `draft:${id}`;
}

function readDraft(id: string): DraftPayload | null {
  try {
    const raw = localStorage.getItem(draftKey(id));
    return raw ? (JSON.parse(raw) as DraftPayload) : null;
  } catch {
    return null;
  }
}

/** Detect a stale-write 409 from either a returned body or a thrown error. */
function detectStale(res?: UpdateResult, err?: unknown): { stale: boolean; current?: NodeDoc } {
  if (res && res.error === 'stale') return { stale: true, current: res.current };
  if (err && typeof err === 'object') {
    const e = err as Record<string, any>;
    const status = e.status ?? e.statusCode ?? e.response?.status;
    const body = e.body ?? e.data ?? e.response?.data ?? e.response?.body;
    if (status === 409) return { stale: true, current: body?.current };
    if (body && body.error === 'stale') return { stale: true, current: body.current };
  }
  return { stale: false };
}

export default function DocEdit() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const client = api as unknown as WikiApi;
  const { patchNode } = useTree();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('');
  const [body, setBody] = useState<JSONContent | null>(null);
  const [contentType, setContentType] = useState<'tiptap' | 'html'>('tiptap');
  const [html, setHtml] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [stale, setStale] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState<DraftPayload | null>(null);
  const [editorKey, setEditorKey] = useState(0); // bump to remount editor with fresh content

  // Refs mirror state for use inside timers without re-subscribing.
  const titleRef = useRef('');
  const statusRef = useRef('');
  const bodyRef = useRef<JSONContent | null>(null);
  const htmlRef = useRef('');
  const contentTypeRef = useRef<'tiptap' | 'html'>('tiptap');
  const etagRef = useRef<string | undefined>(undefined);
  const versionRef = useRef<number | undefined>(undefined);
  const dirtyRef = useRef(false);
  const staleRef = useRef(false);
  const savingRef = useRef(false);
  const localTimer = useRef<number | undefined>(undefined);

  // ---------------------------- load ----------------------------
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const node = await client.getNode(id);
        if (!alive) return;
        applyServerNode(node);
        // Offer to restore a newer local draft.
        const draft = readDraft(id);
        const serverTime = node.updatedAt ? Date.parse(node.updatedAt) : 0;
        if (draft && draft.savedAt > serverTime + 500) {
          setDraftPrompt(draft);
        }
      } catch (e) {
        if (alive) setLoadError(e instanceof Error ? e.message : 'Failed to load document');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function applyServerNode(node: NodeDoc, remount = true) {
    const ct = node.contentType === 'html' ? 'html' : 'tiptap';
    setTitle(node.title ?? '');
    setStatus(node.status ?? '');
    setBody(node.body ?? null);
    setContentType(ct);
    setHtml(node.html ?? '');
    titleRef.current = node.title ?? '';
    statusRef.current = node.status ?? '';
    bodyRef.current = node.body ?? null;
    htmlRef.current = node.html ?? '';
    contentTypeRef.current = ct;
    etagRef.current = node._etag;
    versionRef.current = node.version;
    dirtyRef.current = false;
    staleRef.current = false;
    setStale(false);
    setSaveState('idle');
    if (remount) setEditorKey((k) => k + 1);
  }

  // ---------------------------- persistence ----------------------------
  const writeDraft = useCallback(() => {
    const payload: DraftPayload = {
      title: titleRef.current,
      status: statusRef.current,
      body: bodyRef.current,
      contentType: contentTypeRef.current,
      html: htmlRef.current,
      savedAt: Date.now(),
      baseVersion: versionRef.current,
    };
    try {
      localStorage.setItem(draftKey(id), JSON.stringify(payload));
    } catch {
      /* storage full / unavailable — non-fatal */
    }
  }, [id]);

  const saveToServer = useCallback(
    async (manual: boolean): Promise<boolean> => {
      if (staleRef.current) return false;
      if (savingRef.current) return false;
      if (!dirtyRef.current && !manual) return false;

      savingRef.current = true;
      setSaveState('saving');
      let res: UpdateResult | undefined;
      try {
        const patch =
          contentTypeRef.current === 'html'
            ? {
                title: titleRef.current,
                status: statusRef.current.trim(),
                html: htmlRef.current,
                contentType: 'html' as const,
                expectedEtag: etagRef.current,
              }
            : {
                title: titleRef.current,
                status: statusRef.current.trim(),
                body: bodyRef.current,
                expectedEtag: etagRef.current,
              };
        res = await client.updateNode(id, patch);
        const { stale: isStale } = detectStale(res);
        if (isStale) {
          staleRef.current = true;
          setStale(true);
          setSaveState('conflict');
          return false;
        }
        // success
        etagRef.current = res._etag ?? etagRef.current;
        versionRef.current = res.version ?? versionRef.current;
        dirtyRef.current = false;
        setSaveState('saved');
        // Push the saved title/status into the shared tree so the sidebar and breadcrumbs
        // re-label immediately. Without this they hold the copy they fetched on mount and
        // keep showing the OLD title until a hard reload — the sidebar never unmounts.
        patchNode(id, { title: res.title, status: res.status });
        // Server is now the source of truth; the local draft is redundant.
        try {
          localStorage.removeItem(draftKey(id));
        } catch {
          /* ignore */
        }
        return true;
      } catch (e) {
        const { stale: isStale } = detectStale(undefined, e);
        if (isStale) {
          staleRef.current = true;
          setStale(true);
          setSaveState('conflict');
        } else {
          setSaveState('error');
        }
        return false;
      } finally {
        savingRef.current = false;
      }
    },
    [client, id, patchNode],
  );

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    if (!staleRef.current) setSaveState('unsaved');
    if (localTimer.current) window.clearTimeout(localTimer.current);
    localTimer.current = window.setTimeout(writeDraft, AUTOSAVE_LOCAL_MS);
  }, [writeDraft]);

  // Periodic server autosave.
  useEffect(() => {
    const iv = window.setInterval(() => {
      void saveToServer(false);
    }, AUTOSAVE_SERVER_MS);
    return () => window.clearInterval(iv);
  }, [saveToServer]);

  // Flush the draft to localStorage on unmount so nothing is lost.
  useEffect(() => {
    return () => {
      if (localTimer.current) window.clearTimeout(localTimer.current);
      if (dirtyRef.current) writeDraft();
    };
  }, [writeDraft]);

  // ---------------------------- change handlers ----------------------------
  const onTitleChange = (v: string) => {
    setTitle(v);
    titleRef.current = v;
    markDirty();
  };

  const onStatusChange = (v: string) => {
    setStatus(v);
    statusRef.current = v;
    markDirty();
  };

  const onBodyChange = useCallback(
    (json: JSONContent) => {
      bodyRef.current = json;
      markDirty();
    },
    [markDirty],
  );

  const onHtmlChange = useCallback(
    (v: string) => {
      setHtml(v);
      htmlRef.current = v;
      markDirty();
    },
    [markDirty],
  );

  // ---------------------------- actions ----------------------------
  const handleSave = useCallback(() => {
    void saveToServer(true);
  }, [saveToServer]);

  const handleDone = useCallback(async () => {
    const ok = await saveToServer(true);
    // On a conflict we keep the user here with the banner shown; otherwise go to the doc.
    if (ok || !staleRef.current) navigate(`/doc/${id}`);
  }, [saveToServer, navigate, id]);

  const reloadFromServer = useCallback(async () => {
    try {
      const node = await client.getNode(id);
      applyServerNode(node);
    } catch {
      setSaveState('error');
    }
  }, [client, id]);

  const restoreDraft = () => {
    if (!draftPrompt) return;
    setTitle(draftPrompt.title);
    setStatus(draftPrompt.status ?? '');
    setBody(draftPrompt.body);
    titleRef.current = draftPrompt.title;
    statusRef.current = draftPrompt.status ?? '';
    bodyRef.current = draftPrompt.body;
    if (draftPrompt.contentType === 'html') {
      const draftHtml = draftPrompt.html ?? '';
      setContentType('html');
      setHtml(draftHtml);
      contentTypeRef.current = 'html';
      htmlRef.current = draftHtml;
    }
    dirtyRef.current = true;
    setSaveState('unsaved');
    setEditorKey((k) => k + 1);
    setDraftPrompt(null);
  };

  const discardDraft = () => {
    try {
      localStorage.removeItem(draftKey(id));
    } catch {
      /* ignore */
    }
    setDraftPrompt(null);
  };

  // ---------------------------- render ----------------------------
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-faint">
        <Loader2 size={16} className="mr-2 animate-spin" /> Loading…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center">
        <p className="text-sm text-ink-muted">{loadError}</p>
        <button type="button" onClick={reloadFromServer} className="btn btn-outline mt-4">
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      {/* Editor column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <button
            type="button"
            onClick={() => navigate(`/doc/${id}`)}
            className="btn btn-ghost"
            title="Back to document"
          >
            <ArrowLeft size={16} />
          </button>

          <SaveIndicator state={saveState} />

          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowHistory((s) => !s)}
              className={clsx('btn btn-ghost', showHistory && 'bg-surface-3 text-ink')}
              title="Version history"
            >
              <HistoryIcon size={16} /> History
            </button>
            <button type="button" onClick={handleSave} className="btn btn-outline" title="Save (⌘S)">
              <Save size={14} /> Save
            </button>
            <button type="button" onClick={handleDone} className="btn btn-primary">
              <Check size={14} /> Done
            </button>
          </div>
        </header>

        {/* Banners */}
        {stale && (
          <Banner tone="warn">
            <span>This changed elsewhere — reload to see the latest. Your edits are kept here.</span>
            <button type="button" onClick={reloadFromServer} className="btn btn-outline ml-auto">
              <RefreshCw size={14} /> Reload
            </button>
          </Banner>
        )}
        {draftPrompt && (
          <Banner tone="info">
            <span>
              You have an unsaved draft from {new Date(draftPrompt.savedAt).toLocaleString()}.
            </span>
            <span className="ml-auto flex gap-1.5">
              <button type="button" onClick={restoreDraft} className="btn btn-primary">
                Restore draft
              </button>
              <button type="button" onClick={discardDraft} className="btn btn-ghost">
                Discard
              </button>
            </span>
          </Banner>
        )}

        {/* Document body */}
        {contentType === 'html' ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center gap-3 border-b border-border px-6 py-4">
              <input
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder="Untitled"
                aria-label="Document title"
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-2xl font-bold text-ink outline-none placeholder:text-ink-faint focus:outline-none"
              />
              <StatusField value={status} onChange={onStatusChange} />
            </div>
            <div className="grid min-h-0 flex-1 grid-rows-2 lg:grid-cols-2 lg:grid-rows-1">
              {/* Source */}
              <div className="flex min-h-0 flex-col border-b border-border lg:border-b-0 lg:border-r">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-ink-faint">
                  HTML source
                </div>
                <textarea
                  value={html}
                  onChange={(e) => onHtmlChange(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                      e.preventDefault();
                      handleSave();
                    }
                  }}
                  spellCheck={false}
                  aria-label="HTML source"
                  placeholder="<!doctype html>…"
                  className="min-h-0 w-full flex-1 resize-none border-0 bg-surface p-4 font-mono text-sm leading-6 text-ink outline-none placeholder:text-ink-faint focus:outline-none"
                />
              </div>
              {/* Preview */}
              <div className="flex min-h-0 flex-col">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-ink-faint">
                  Preview
                </div>
                <iframe
                  srcDoc={html}
                  title="HTML preview"
                  sandbox="allow-scripts allow-popups allow-forms"
                  className="min-h-0 w-full flex-1 border-0 bg-white"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-[1080px] px-4 py-8 sm:px-6">
              <div className="mb-4 flex items-center gap-3">
                <input
                  value={title}
                  onChange={(e) => onTitleChange(e.target.value)}
                  placeholder="Untitled"
                  aria-label="Document title"
                  className="min-w-0 flex-1 border-0 bg-transparent p-0 text-3xl font-bold text-ink outline-none placeholder:text-ink-faint focus:outline-none"
                />
                <StatusField value={status} onChange={onStatusChange} />
              </div>
              <Editor
                key={editorKey}
                initialContent={body}
                onChange={onBodyChange}
                onSave={handleSave}
              />
            </div>
          </div>
        )}
      </div>

      {/* History panel */}
      {showHistory && (
        <VersionHistory
          nodeId={id}
          onClose={() => setShowHistory(false)}
          onRestored={reloadFromServer}
        />
      )}
    </div>
  );
}

/* ------------------------------ bits ------------------------------ */

/**
 * Small metadata field for the page's status badge (e.g. beta, live, draft).
 * Shows a live preview of the badge as rendered in the sidebar/doc view.
 */
function StatusField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <span className="flex shrink-0 items-center gap-2">
      <StatusBadge status={value} />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="status"
        aria-label="Page status (badge)"
        title="Status badge shown next to the title (e.g. beta, live, draft). Leave empty for none."
        className="w-24 rounded border border-border bg-surface px-2 py-1 text-xs text-ink-muted outline-none placeholder:text-ink-faint focus:border-accent"
      />
    </span>
  );
}

function SaveIndicator({ state }: { state: SaveState }) {
  const map: Record<SaveState, { icon: React.ReactNode; text: string; cls: string }> = {
    idle: { icon: <Cloud size={14} />, text: 'Saved', cls: 'text-ink-faint' },
    unsaved: { icon: <Cloud size={14} />, text: 'Unsaved changes', cls: 'text-ink-faint' },
    saving: {
      icon: <Loader2 size={14} className="animate-spin" />,
      text: 'Saving…',
      cls: 'text-ink-faint',
    },
    saved: { icon: <Check size={14} />, text: 'Saved', cls: 'text-ink-faint' },
    conflict: { icon: <RefreshCw size={14} />, text: 'Conflict', cls: 'text-amber-600' },
    error: { icon: <RefreshCw size={14} />, text: 'Save failed', cls: 'text-red-600' },
  };
  const s = map[state];
  return (
    <span className={clsx('flex items-center gap-1.5 text-xs', s.cls)}>
      {s.icon}
      {s.text}
    </span>
  );
}

function Banner({ tone, children }: { tone: 'warn' | 'info'; children: React.ReactNode }) {
  return (
    <div
      className={clsx(
        'flex items-center gap-3 border-b px-4 py-2 text-sm',
        tone === 'warn'
          ? 'border-amber-300/60 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
          : 'border-border bg-surface-2 text-ink',
      )}
    >
      {children}
    </div>
  );
}
